import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, Info } from 'lucide-react';
import { db, analytics } from './firebase/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import PrivacyPolicy from './PrivacyPolicy';

const RequestForm = ({ domain, onClose }) => {
  const [tgHandleName, settgHandleName] = useState('');
  const [walletAddress, setwalletAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Log an event when the modal is viewed - with minimal data
  useEffect(() => {
    logEvent(analytics, 'view_form_modal', { action: 'view' });
  }, [domain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Log the submit button click event - with minimal data
    logEvent(analytics, 'form_interaction', { action: 'submit' });

    const formData = {
      domain,
      tgHandleName,
      walletAddress,
      submittedAt: new Date(),
    };

    try {
      await addDoc(collection(db, 'requests'), formData);
      setSubmitted(true);
      // Log a successful submission event - with minimal data
      logEvent(analytics, 'form_interaction', { action: 'success' });
    } catch (err) {
      //console.error('Error adding document: ', err);
      setError('Failed to submit request. \nPlease try again.');
      // Log a failed submission event - with minimal data
      logEvent(analytics, 'form_interaction', { action: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-close the modal after a few seconds if submission succeeded or failed.
  useEffect(() => {
    if (submitted || error) {
      const timer = setTimeout(() => {
        onClose();
      }, 30000); // 30 seconds delay
      return () => clearTimeout(timer);
    }
  }, [submitted, error, onClose]);

  // Check if inputs are not empty (ignoring white space)
  const isFormValid = tgHandleName.trim() !== '' && walletAddress.trim() !== '';

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
      <div className="bg-black border border-white rounded-lg p-6 w-3/4 sm:w-full sm:max-w-md relative">
        <button
          className="absolute top-2 right-2 text-red-500"
          onClick={onClose}
          aria-label="Close form"
        >
          <X size={20} />
        </button>
        {submitted ? (
          // Success view
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="text-green-500" size={48} />
            <h2 className="text-base sm:text-2xl font-semibold text-white text-center mt-4">
              We have received your request!
            </h2>
            <br></br>
            <p className="text-sm sm:text-xl text-white text-center">
              One of our associates will get back to you shortly! You will be contacted on Telegram at the handle you provided.
            </p>
          </div>
        ) : error ? (
          // Error view
          <div className="flex flex-col items-center justify-center py-8">
            <XCircle className="text-red-500" size={48} />
            <h2 className="text-base sm:text-2xl font-semibold text-white mt-4">
              <p style={{ whiteSpace: 'pre-line' }} className="text-white text-center">
                {error}
              </p>
            </h2>
          </div>
        ) : (
          // Form view
          <>
            <h2 className="text-xl font-semibold mb-4 text-white">Request Domain</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-white">Domain</label>
                <input
                  type="text"
                  value={domain}
                  readOnly
                  className="w-full border border-gray-700 rounded p-2 bg-gray-800 text-white"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-white">TG Handle Name</label>
                <input
                  type="text"
                  value={tgHandleName}
                  onChange={(e) => settgHandleName(e.target.value)}
                  className="w-full border border-gray-700 rounded p-2 bg-gray-800 text-white"
                  placeholder="Enter your TG handle with @"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-white">Wallet Address</label>
                <input
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setwalletAddress(e.target.value)}
                  className="w-full border border-gray-700 rounded p-2 bg-gray-800 text-white"
                  placeholder="Enter your wallet address"
                />
              </div>

              <button
              type="submit"
              disabled={loading || !isFormValid}
              className="w-full h-12 bg-white text-black rounded p-2 hover:bg-gray-200 transition flex items-center justify-center disabled:cursor-not-allowed disabled:bg-gray-500 disabled:text-gray-300 disabled:hover:bg-gray-500"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestForm;